import { client } from '../apollo';
import { getAuthStateFromToken } from './auth';
import { AuthState } from '../containers/shared/auth/AuthContext';
import {
  ALL_ASSIGNED_REGISTRATIONS,
  ALL_ASSIGNED_INSTITUTIONS,
  ALL_ECD_REQUIRED_DOCUMENTS,
  ALL_INSTITUTION_TYPES,
  GET_ECD_NON_CENTER_BASED_SURVEY,
  GET_ECD_PARTIAL_CARE_FACILITY_SURVEY,
  GET_MINISTRIES,
  ALL_COUNTRIES,
  ALL_PROVINCES,
  ALL_DISTRICT_MUNICIPALITIES,
  ALL_LOCAL_MUNICIPALITIES,
  UPSERT_ECD_REGISTRATION,
  UPSERT_INSTITUTION,
  ALL_ECD_REGISTRATION_TYPES,
  GET_SIMPLE_USER,
  ALL_ASSIGNED_INSTITUTIONS_PEOPLE,
  UPSERT_PERSON,
  ALL_HOME_LANGUAGES,
  ALL_NATIONALITIES,
  ALL_GENDERS,
  ALL_POPULATION_GROUPS,
  ALL_DISABILITY_TYPES,
  ALL_STAKEHOLDER_TYPES,
  ALL_GRADES,
  GET_NOTIFICATIONS,
  FILTERED_ECD_DISTRICTS,
  // ALL_INSTITUTION_PORTAL_USERS,
} from '../graphql';
import {
  deleteChangedInstitutionsList,
  deleteChangedRegistrationsList,
  deepCopy,
  formatInstitutionData,
  getChangedInstitutionsList,
  getChangedRegistrationsList,
  stripSelectValues,
  writeECDRegistrationsList,
  writeECDRequiredDocumentsList,
  writeInstitutionsList,
  writeMinistries,
  writeNCBSurvey,
  writePCFSurvey,
  writeCountries,
  writeProvinces,
  writeDistrictMunicipalities,
  writeLocalMunicipalities,
  writeECDRegistrationTypes,
  formatECDRegistrationData,
  writeUserInformation,
} from '.';
import {
  deleteChangedPeopleList,
  getAllCountries,
  getAllDisabilityTypes,
  getAllDistrictMunicipalities,
  getAllDistricts,
  getAllGenders,
  getAllGrades,
  getAllHomeLanguages,
  // getAllInstitutionPortalUsers,
  getAllInstitutionTypes,
  getAllLocalMunicipalities,
  getAllMinistries,
  getAllNationalities,
  getAllPeopleTypes,
  getAllPopulationGroups,
  getAllProvinces,
  getChangedPeopleList,
  getECDRegistrationTypes,
  writeDisabilityTypes,
  writeDistricts,
  writeGenders,
  writeGrades,
  writeHomeLanguages,
  // writeInstitutionPortalUsers,
  writeInstitutionTypes,
  writeNationalities,
  writeNotificationsList,
  writePeopleList,
  writePeopleTypes,
  writePopulationGroups,
} from './idbFns';

type ArgsType = {
  initial: any[];
  first: number;
  last: number | null;
  after: string | null;
  before: string | null;
  skip: number | null;
};

type FetchFunction = (args: ArgsType) => Promise<any[]>;

const fetchRegistrations: FetchFunction = async ({ initial, first, last, after, before, skip }: ArgsType) => {
  const {
    data: { ecdRegistrations },
  }: any = await client.query({
    query: ALL_ASSIGNED_REGISTRATIONS,
    variables: { input: { first, last, after, before, skip } },
  });

  if (ecdRegistrations && ecdRegistrations.pageInfo && ecdRegistrations.pageInfo.hasNextPage) {
    return fetchRegistrations({
      initial: [...initial, ...ecdRegistrations.edges],
      first,
      last,
      after: ecdRegistrations.pageInfo.endCursor,
      before,
      skip,
    });
  }

  return [...initial, ...ecdRegistrations.edges];
};

const fetchInstitutions: FetchFunction = async ({ initial, first, last, after, before, skip }: ArgsType) => {
  const {
    data: { institutions },
  }: any = await client.query({
    query: ALL_ASSIGNED_INSTITUTIONS,
    variables: { input: { first, last, after, before, skip } },
  });

  if (institutions && institutions.pageInfo && institutions.pageInfo.hasNextPage) {
    return fetchInstitutions({
      initial: [...initial, ...institutions.edges],
      first,
      last,
      after: institutions.pageInfo.endCursor,
      before,
      skip,
    });
  }

  return [...initial, ...institutions.edges];
};

const fetchInstitutionsPeople: FetchFunction = async ({ initial, first, last, after, before, skip }: ArgsType) => {
  const {
    data: { people },
  }: any = await client.query({
    query: ALL_ASSIGNED_INSTITUTIONS_PEOPLE,
    variables: { input: { first, last, after, before, skip } },
  });

  if (people && people.pageInfo && people.pageInfo.hasNextPage) {
    return fetchInstitutionsPeople({
      initial: [...initial, ...people.edges],
      first,
      last,
      after: people.pageInfo.endCursor,
      before,
      skip,
    });
  }

  return [...initial, ...people.edges];
};

const fetchUserNotifications: FetchFunction = async ({ initial, first, last, after, before, skip }: ArgsType) => {
  const {
    data: { notifications },
  }: any = await client.query({
    query: GET_NOTIFICATIONS,
    variables: { input: { first, last, after, before, skip } },
  });

  if (notifications && notifications.pageInfo && notifications.pageInfo.hasNextPage) {
    return fetchUserNotifications({
      initial: [...initial, ...notifications.edges],
      first,
      last,
      after: notifications.pageInfo.endCursor,
      before,
      skip,
    });
  }

  return [...initial, ...notifications.edges];
};

const fetchAllECDRegistrations = async () => {
  const allRegistrations: any[] = await fetchRegistrations({
    initial: [],
    first: 30,
    last: null,
    after: null,
    before: null,
    skip: null,
  });
  const decoded: AuthState = getAuthStateFromToken();
  const userID = decoded && decoded.user.id;

  // add userID obtained from token
  return writeECDRegistrationsList(allRegistrations, userID);
};

const fetchAllInstitutions = async () => {
  const allInstitutions: any[] = await fetchInstitutions({
    initial: [],
    first: 30,
    last: null,
    after: null,
    before: null,
    skip: null,
  });
  const decoded: AuthState = getAuthStateFromToken();
  const userID = decoded && decoded.user.id;

  // add userID obtained from token
  return writeInstitutionsList(allInstitutions, userID);
};

const fetchAllInstitutionsPeople = async () => {
  const allInstitutionsPeople: any[] = await fetchInstitutionsPeople({
    initial: [],
    first: 200,
    last: null,
    after: null,
    before: null,
    skip: null,
  });
  const decoded: AuthState = getAuthStateFromToken();
  const userID = decoded && decoded.user.id;

  // add userID obtained from token
  return writePeopleList(allInstitutionsPeople, userID);
};

const fetchAllUserNotification = async () => {
  const allNotifications: any[] = await fetchUserNotifications({
    initial: [],
    first: 200,
    last: null,
    after: null,
    before: null,
    skip: null,
  });
  const decoded: AuthState = getAuthStateFromToken();
  const userID = decoded && decoded.user.id;

  // add userID obtained from token
  return writeNotificationsList(allNotifications, userID);
};

const fetchAllRequiredDocuments = async () => {
  const {
    data: { documents },
  }: any = await client.query({ query: ALL_ECD_REQUIRED_DOCUMENTS });

  return writeECDRequiredDocumentsList(documents);
};

const fetchNonCenterBasedSurvey = async () => {
  const {
    data: { survey },
  }: any = await client.query({ query: GET_ECD_NON_CENTER_BASED_SURVEY });

  return writeNCBSurvey(survey);
};

const fetchPartialCareFacilitySurvey = async () => {
  const {
    data: { survey },
  }: any = await client.query({ query: GET_ECD_PARTIAL_CARE_FACILITY_SURVEY });

  return writePCFSurvey(survey);
};

const fetchAllMinistries = async () => {
  const data = await getAllMinistries();
  if (!!data || (data && data.length > 0)) return;
  const {
    data: { ministries },
  }: any = await client.query({ query: GET_MINISTRIES });
  return writeMinistries(ministries);
};

const fetchAllCountries = async () => {
  const data = await getAllCountries();
  if (!!data || (data && data.length > 0)) return;
  const {
    data: { countries },
  }: any = await client.query({ query: ALL_COUNTRIES, variables: { input: {} } });
  return writeCountries(countries);
};

const fetchAllProvinces = async () => {
  const data = await getAllProvinces();
  if (!!data || (data && data.length > 0)) return;
  const {
    data: { provinces },
  }: any = await client.query({ query: ALL_PROVINCES, variables: { input: {} } });
  return writeProvinces(provinces);
};

const fetchAllDistrictMunicipalities = async () => {
  const data = await getAllDistrictMunicipalities();
  if (!!data || (data && data.length > 0)) return;
  const {
    data: { municipalities },
  }: any = await client.query({ query: ALL_DISTRICT_MUNICIPALITIES, variables: { input: {} } });
  return writeDistrictMunicipalities(municipalities);
};

const fetchAllLocalMunicipalities = async () => {
  const data = await getAllLocalMunicipalities();
  if (!!data || (data && data.length > 0)) return;
  const {
    data: { municipalities },
  }: any = await client.query({ query: ALL_LOCAL_MUNICIPALITIES, variables: { input: {} } });

  return writeLocalMunicipalities(municipalities);
};

const fetchAllDistricts = async () => {
  const data = await getAllDistricts();
  if (!!data || (data && data.length > 0)) return;
  const {
    data: { districts },
  }: any = await client.query({ query: FILTERED_ECD_DISTRICTS, variables: { input: {} } });

  return writeDistricts(districts);
};

// const fetchAllInstitutionPortalUsers = async () => {
//   const data = await getAllInstitutionPortalUsers();
//   if (!!data || (data && data.length > 0)) return;
//   const {
//     data: { users },
//   }: any = await client.query({ query: ALL_INSTITUTION_PORTAL_USERS, variables: { input: {} } });

//   return writeInstitutionPortalUsers(users);
// };

const fetchAllInstitutionTypes = async () => {
  const data = await getAllInstitutionTypes();
  if (!!data || (data && data.length > 0)) return;
  const {
    data: { institutionTypes },
  }: any = await client.query({ query: ALL_INSTITUTION_TYPES, variables: { input: {} } });
  return writeInstitutionTypes(institutionTypes);
};

const fetchAllHomeLanguages = async () => {
  const data = await getAllHomeLanguages();
  if (!!data || (data && data.length > 0)) return;
  const {
    data: { homeLanguages },
  }: any = await client.query({ query: ALL_HOME_LANGUAGES, variables: { input: {} } });
  return writeHomeLanguages(homeLanguages);
};

const fetchAllNationalities = async () => {
  const data = await getAllNationalities();
  if (!!data || (data && data.length > 0)) return;
  const {
    data: { nationalities },
  }: any = await client.query({ query: ALL_NATIONALITIES, variables: { input: {} } });
  return writeNationalities(nationalities);
};

const fetchAllGenders = async () => {
  const data = await getAllGenders();
  if (!!data || (data && data.length > 0)) return;
  const {
    data: { genders },
  }: any = await client.query({ query: ALL_GENDERS, variables: { input: {} } });
  return writeGenders(genders);
};

const fetchAllPopulationGroups = async () => {
  const data = await getAllPopulationGroups();
  if (!!data || (data && data.length > 0)) return;
  const {
    data: { populationGroups },
  }: any = await client.query({ query: ALL_POPULATION_GROUPS, variables: { input: {} } });
  return writePopulationGroups(populationGroups);
};

const fetchAllDisabilityTypes = async () => {
  const data = await getAllDisabilityTypes();
  if (!!data || (data && data.length > 0)) return;
  const {
    data: { disabilityTypes },
  }: any = await client.query({ query: ALL_DISABILITY_TYPES });
  return writeDisabilityTypes(disabilityTypes);
};

const fetchAllPeopleTypes = async () => {
  const data = await getAllPeopleTypes();
  if (!!data || (data && data.length > 0)) return;
  const {
    data: { stakeholderTypes },
  }: any = await client.query({ query: ALL_STAKEHOLDER_TYPES, variables: { input: {} } });
  return writePeopleTypes(stakeholderTypes);
};

const fetchAllGrades = async () => {
  const data = await getAllGrades();
  if (!!data || (data && data.length > 0)) return;
  const {
    data: { grades },
  }: any = await client.query({ query: ALL_GRADES, variables: { input: {} } });
  return writeGrades(grades);
};

/*
const fetchPortalOrganisation = async (portal?: string) => {
  const decoded: AuthState = getAuthStateFromToken();
  if (!portal) portal = decoded && decoded.defaultRedirectRoute;
  const { data }: any = await client.query({ query: GET_PORTAL_ORGANISATION, variables: { portal: portal } });
  return writePortalOrganisation(data);
};
*/

const fetchAllECDRegistrationTypes = async () => {
  const data = await getECDRegistrationTypes();
  if (!!data || (data && data.length > 0)) return;
  const {
    data: { registrationTypes },
  }: any = await client.query({ query: ALL_ECD_REGISTRATION_TYPES });
  return writeECDRegistrationTypes(registrationTypes);
};

export const pwaOfflineQueryFetch = (userId: string) => {
  if (
    ['cks76e8wk04yv0786do4ep50e', 'cks751slv03os0786x1zolljj'].includes(userId) ||
    window.location.origin === 'https://rmt.ecd.gov.za'
  ) {
    fetchAllECDRegistrations();
  }
  fetchAllRequiredDocuments();
  fetchAllInstitutions();
  fetchNonCenterBasedSurvey();
  fetchPartialCareFacilitySurvey();
  // global
  fetchAllMinistries();
  fetchAllInstitutionTypes();
  fetchAllCountries();
  fetchAllProvinces();
  fetchAllDistrictMunicipalities();
  fetchAllLocalMunicipalities();
  fetchAllECDRegistrationTypes();
  fetchAllNationalities();
  fetchAllGenders();
  fetchAllPeopleTypes();
  fetchAllPopulationGroups();
  fetchAllHomeLanguages();
  fetchAllDisabilityTypes();
  fetchAllGrades();
  fetchAllDistricts();
  // fetchAllInstitutionPortalUsers();
  // user
  fetchUserInformation();
  fetchAllInstitutionsPeople();
  fetchAllUserNotification();
};

// POST operations
const syncInstitutionsData = async () => {
  const decoded: AuthState = getAuthStateFromToken();
  const userID = decoded && decoded.user.id;
  // get all updated or new institutions from indexedDB
  const changedInstitutions = await getChangedInstitutionsList(userID);
  const response =
    changedInstitutions &&
    Array.isArray(changedInstitutions) &&
    (await Promise.all(
      changedInstitutions.map(async ({ node }: any) => {
        const copyOfInstitution = Object.assign({}, node);
        const formattedData = formatInstitutionData(copyOfInstitution);
        const strippedData: any = stripSelectValues(formattedData);

        strippedData.appliedForEcdEsrf =
          strippedData.ecdEmploymentStimulus === 'Applied for ECD-ESRF' ||
          strippedData.ecdEmploymentStimulus === 'Received ECD-ESRF';
        strippedData.receivedEcdEsrf = strippedData.ecdEmploymentStimulus === 'Received ECD-ESRF';
        strippedData.ecdFacilityDetail = stripSelectValues(strippedData.ecdFacilityDetail);
        delete strippedData.ecdEmploymentStimulus;
        delete strippedData.ecdRegistrationProgress;
        delete strippedData.ecdEmploymentStimulus;
        delete strippedData.ecdEnrolmentDataViewOption;
        delete strippedData.ecdStaffDataViewOption;
        delete strippedData.newContact;
        delete strippedData.userOrStakeholder;
        delete strippedData.nutritionYear;
        delete strippedData.ecdFinanceDetailsYear;

        if (strippedData.ecdEnrolmentDataCurrent) {
          delete strippedData.ecdEnrolmentDataCurrent.year;
        }
        if (strippedData.ecdEnrolmentData10Day) {
          delete strippedData.ecdEnrolmentData10Day.year;
        }
        if (strippedData.ecdStaffDataCurrent) {
          delete strippedData.ecdStaffDataCurrent.year;
        }
        if (strippedData.ecdStaffData10Day) {
          delete strippedData.ecdStaffData10Day.year;
        }
        if ('mealsProvided' in strippedData) {
          strippedData['mealsProvided'] = strippedData.mealsProvided ? strippedData.mealsProvided.toString() : '';
        }
        if ('foodProvider' in strippedData) {
          strippedData['foodProvider'] = strippedData.foodProvider ? strippedData.foodProvider.toString() : '';
        }
        if ('languageOfLearning' in strippedData) {
          strippedData['languageOfLearning'] = strippedData.languageOfLearning
            ? strippedData.languageOfLearning.toString()
            : '';
        }
        if ('registeredAsNpo' in strippedData) {
          strippedData['registeredAsNpo'] = strippedData.registeredAsNpo && parseInt(strippedData.registeredAsNpo);
        }
        if ('numberOfDaysServiceOperate' in strippedData) {
          strippedData['numberOfDaysServiceOperate'] = strippedData.numberOfDaysServiceOperate
            ? strippedData.numberOfDaysServiceOperate.toString()
            : '';
        }

        return await client
          .mutate({
            mutation: UPSERT_INSTITUTION,
            variables: {
              input: strippedData,
            },
          })
          .then(response =>
            console.info(
              `Successfully uploaded "${response.data.institution.name}", ID: ${response.data.institution.id}`
            )
          )
          .catch(err => console.error(`ERROR Uploading "${node.name}"!: ${err}`));
      })
    ));
  // response and clear changed institutions from indexed db
  return response && deleteChangedInstitutionsList(userID);
};

const syncRegistrationData = async () => {
  const decoded: AuthState = getAuthStateFromToken();
  const userID = decoded && decoded.user.id;
  // get all updated or new registrations from indexedDB
  const changedRegistrations = await getChangedRegistrationsList(userID);
  const response =
    changedRegistrations &&
    Array.isArray(changedRegistrations) &&
    (await Promise.all(
      changedRegistrations.map(async ({ node }: any) => {
        const copyOfRegistration = deepCopy(node);
        const formattedData = formatECDRegistrationData(copyOfRegistration);
        const { id, submitted, stepApplication, stepFramework, stepAssessment, stepApproval } = formattedData;
        const flattedObj = { id, submitted, ...stepApplication, ...stepFramework, ...stepAssessment, ...stepApproval };

        delete flattedObj.exportPDFTitle;
        delete flattedObj.approvalStatus;
        delete flattedObj.reasonForRejection;
        delete flattedObj.progReasonForRejection;

        const strippedData: any = stripSelectValues(flattedObj);

        strippedData.frameworkAnswers = JSON.stringify(copyOfRegistration.frameworkAnswers);
        strippedData.providedDocuments = copyOfRegistration.providedDocuments;
        strippedData.id = strippedData.id.startsWith('create') ? '' : strippedData.id;

        await client
          .mutate({
            mutation: UPSERT_ECD_REGISTRATION,
            variables: {
              input: strippedData,
            },
          })
          .then(response =>
            console.info(`Successfully uploaded ECD Registration ID: ${response.data.ecdRegistration.id}`)
          )
          .catch(err => console.error(`ERROR Uploading ECD Registration!: ${err}`));
      })
    ));
  // response and clear changed registrations from indexed db
  return response && deleteChangedRegistrationsList(userID);
};

const syncPeopleData = async () => {
  const decoded: AuthState = getAuthStateFromToken();
  const userID = decoded && decoded.user.id;
  // get all updated or new institutions from indexedDB
  const changedPeople = await getChangedPeopleList(userID);
  const response =
    changedPeople &&
    Array.isArray(changedPeople) &&
    (await Promise.all(
      changedPeople.map(async ({ node }: any) => {
        const copyOfPerson = Object.assign({}, node);
        copyOfPerson.associatedInstitutions = node.associatedInstitutions.map((a: any) => Object.assign({}, a));

        copyOfPerson.associatedOrganisations = node.associatedOrganisations.map((a: any) => Object.assign({}, a));

        const formattedValues: any = stripSelectValues(copyOfPerson);
        formattedValues.associatedInstitutions = formattedValues.associatedInstitutions.map((i: any) => {
          return {
            ...i,
            id: i.id.startsWith('create') ? '' : i.id,
            institution: i.institution.id,
          };
        });
        formattedValues.associatedOrganisations = formattedValues.associatedOrganisations.map((i: any) => {
          return {
            ...i,
            id: i.id.startsWith('create') ? '' : i.id,
            organisation: i.organisation.id,
          };
        });

        formattedValues.caregiverList = formattedValues.caregiverList.map((i: any) => {
          return {
            ...i,
            id: i.id.startsWith('create') ? '' : i.id,
          };
        });

        delete formattedValues.emisNumber;
        delete formattedValues.ministryId;
        delete formattedValues.formType;
        delete formattedValues.portal;
        delete formattedValues.nosubmit;

        if (formattedValues.caregiver) {
          delete formattedValues.caregiver;
        }

        if (formattedValues.contactNumberCountry) {
          delete formattedValues.contactNumberCountry;
        }
        if (formattedValues.careGiverContactNumberCountry) {
          delete formattedValues.careGiverContactNumberCountry;
        }

        const { id, ...rest } = formattedValues;

        return await client
          .mutate({
            mutation: UPSERT_PERSON,
            variables: {
              input: {
                ...rest,
                id: id && id.startsWith('create') ? '' : id,
              },
            },
          })
          .then(response => {
            console.info(`Successfully uploaded "${response.data.people.firstName}", ID: ${response.data.people.id}`);
          })
          .catch(err => console.error(`ERROR Uploading "${node.firstName}"!: ${err}`));
      })
    ));
  // response and clear changed institutions from indexed db
  return response && deleteChangedPeopleList(userID);
};

export const fetchUserInformation = async () => {
  const decoded: AuthState = getAuthStateFromToken();
  const userID = decoded && decoded.user.id;

  try {
    const {
      data: { user },
    }: any = await client.query({ query: GET_SIMPLE_USER, variables: { id: userID } });
    user && writeUserInformation(user);
  } catch (err) {
    console.log('err' + err);
  }
};

export const syncPwaOfflineData = () => {
  syncInstitutionsData();
  syncRegistrationData();
  syncPeopleData();
};
